import { create } from 'zustand';

type LocalMediaPreviewSlideState = {
  indexByMediaId: Record<string, number>;
  totalPagesByMediaId: Record<string, number>;
  /** Wall-clock ms when the operator last changed this media in item PreviewModal. */
  operatorTouchedAtByMediaId: Record<string, number>;
  stepSlide: (mediaId: string, delta: number) => void;
  setSlideIndex: (mediaId: string, page: number, options?: { operatorPick?: boolean }) => void;
  reportTotalPages: (mediaId: string, totalPages: number) => void;
  isRecentlyOperatorPicked: (mediaId: string, withinMs?: number) => boolean;
  resetMedia: (mediaId: string) => void;
};

const initialState = {
  indexByMediaId: {} as Record<string, number>,
  totalPagesByMediaId: {} as Record<string, number>,
  operatorTouchedAtByMediaId: {} as Record<string, number>,
};

export const useLocalMediaPreviewSlideStore = create<LocalMediaPreviewSlideState>((set, get) => ({
  ...initialState,
  stepSlide: (mediaId, delta) => {
    const state = get();
    const current = state.indexByMediaId[mediaId] ?? 1;
    const max = state.totalPagesByMediaId[mediaId];
    let next = Math.max(1, current + delta);
    if (max) {
      next = Math.min(next, max);
    }
    set({
      indexByMediaId: { ...state.indexByMediaId, [mediaId]: next },
      operatorTouchedAtByMediaId: {
        ...state.operatorTouchedAtByMediaId,
        [mediaId]: Date.now(),
      },
    });
  },
  setSlideIndex: (mediaId, page, options) => {
    const state = get();
    const max = state.totalPagesByMediaId[mediaId];
    let next = Math.max(1, page);
    if (max) {
      next = Math.min(next, max);
    }
    set({
      indexByMediaId: { ...state.indexByMediaId, [mediaId]: next },
      ...(options?.operatorPick
        ? {
            operatorTouchedAtByMediaId: {
              ...state.operatorTouchedAtByMediaId,
              [mediaId]: Date.now(),
            },
          }
        : {}),
    });
  },
  reportTotalPages: (mediaId, totalPages) => {
    const state = get();
    if (state.totalPagesByMediaId[mediaId] === totalPages) {
      return;
    }
    const current = state.indexByMediaId[mediaId];
    // Do not invent index 1 — open at layer/DB resume, not always slide 1.
    set({
      totalPagesByMediaId: { ...state.totalPagesByMediaId, [mediaId]: totalPages },
      ...(current != null
        ? {
            indexByMediaId: {
              ...state.indexByMediaId,
              [mediaId]: Math.min(current, totalPages),
            },
          }
        : {}),
    });
  },
  isRecentlyOperatorPicked: (mediaId, withinMs = 60_000) => {
    const touchedAt = get().operatorTouchedAtByMediaId[mediaId];
    if (touchedAt == null) {
      return false;
    }
    return Date.now() - touchedAt < withinMs;
  },
  resetMedia: (mediaId) => {
    set((state) => {
      const indexByMediaId = { ...state.indexByMediaId };
      const totalPagesByMediaId = { ...state.totalPagesByMediaId };
      const operatorTouchedAtByMediaId = { ...state.operatorTouchedAtByMediaId };
      delete indexByMediaId[mediaId];
      delete totalPagesByMediaId[mediaId];
      delete operatorTouchedAtByMediaId[mediaId];
      return { indexByMediaId, totalPagesByMediaId, operatorTouchedAtByMediaId };
    });
  },
}));
